"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { io, Socket } from "socket.io-client";
import { api } from "@/lib/api";
import { isTokenExpired } from "@/lib/socket-auth";

interface InternalChatEvents {
  onMessageCreated?: (data: { channelId: string; message: any; echoId?: string }) => void;
  onChannelCreated?: (channel: any) => void;
  onChannelUpdated?: (data: { id: string; name: string; description: string | null }) => void;
  onChannelDeleted?: (data: { channelId: string }) => void;
  onMembersChanged?: (data: { channelId: string; channel: any; addedUserIds: string[]; removedUserIds: string[] }) => void;
  onTyping?: (data: { channelId: string; userId: string; userName?: string; isTyping: boolean }) => void;
  onThreadReply?: (data: { channelId: string; parentId: string; message: any }) => void;
  onReactionChanged?: (data: { messageId: string; reactions: any[] }) => void;
  onMessageUpdated?: (data: { channelId: string; message: any }) => void;
  onMessageDeleted?: (data: { channelId: string; messageId: string }) => void;
  onPresenceChanged?: (data: { userId: string; status: "online" | "away" | "busy" | "on_break" | "offline" }) => void;
}

export function useInternalChatSocket(events?: InternalChatEvents) {
  const socketRef = useRef<Socket | null>(null);
  const eventsRef = useRef(events);
  const [connected, setConnected] = useState(false);
  // Guard pra evitar loop infinito de refresh: se o segundo connect_error
  // tb falhar pos-refresh, dropa em vez de tentar de novo.
  const refreshAttemptedRef = useRef(false);

  // Atualiza ref a cada render — listeners sempre chamam versão mais recente
  eventsRef.current = events;

  useEffect(() => {
    const token = api.getAccessToken();
    if (!token) return;

    const apiUrl = process.env.NEXT_PUBLIC_API_URL
      || (typeof window !== "undefined" ? `${window.location.origin}/api` : "/api");
    const wsUrl = apiUrl.replace("/api", "");

    const socket = io(`${wsUrl}/internal-chat`, {
      auth: { token },
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 3000,
    });

    socket.on("connect", () => {
      setConnected(true);
      refreshAttemptedRef.current = false;
    });
    socket.on("disconnect", () => setConnected(false));

    // Renovação de JWT (mesmo padrão do use-chat-socket): o token é fixado em
    // socket.auth no mount; como o JWT expira em 15min, um reconnect após esse
    // prazo chega expirado e o gateway emite `auth_required` + disconnect
    // (`io server disconnect` — o socket.io NÃO tenta de novo sozinho).
    // Sem este handler o chat interno morre permanentemente até F5.
    const doRefreshAndReconnect = (reason: string) => {
      if (refreshAttemptedRef.current) {
        console.warn(`[internal-chat-socket] refresh ja tentado (reason=${reason})`);
        return;
      }
      refreshAttemptedRef.current = true;
      api
        .refreshTokens()
        .then((ok) => {
          if (!ok) return; // refreshTokens já limpou tokens + redirect /login
          const newToken = api.getAccessToken();
          if (!newToken) return;
          socket.auth = { token: newToken };
          socket.disconnect();
          socket.connect();
        })
        .catch((refreshErr) => {
          console.warn(`[internal-chat-socket] refresh falhou (reason=${reason}):`, refreshErr);
        });
    };

    socket.on("auth_required", (payload: { reason?: string }) => {
      doRefreshAndReconnect(payload?.reason ?? "server_auth_required");
    });

    socket.on("connect_error", (err) => {
      if (!isTokenExpired(api.getAccessToken())) {
        console.warn("[internal-chat-socket] connect_error", err?.message ?? err);
        return;
      }
      doRefreshAndReconnect("client_token_expired");
    });

    socket.on("internal:message:created", (data) => {
      eventsRef.current?.onMessageCreated?.(data);
    });
    socket.on("internal:channel:created", (data) => {
      eventsRef.current?.onChannelCreated?.(data);
    });
    socket.on("internal:channel:updated", (data) => {
      eventsRef.current?.onChannelUpdated?.(data);
    });
    socket.on("internal:channel:deleted", (data) => {
      eventsRef.current?.onChannelDeleted?.(data);
    });
    socket.on("internal:members:changed", (data) => {
      eventsRef.current?.onMembersChanged?.(data);
    });
    socket.on("internal:typing", (data) => {
      eventsRef.current?.onTyping?.(data);
    });
    socket.on("internal:thread:reply", (data) => {
      eventsRef.current?.onThreadReply?.(data);
    });
    socket.on("internal:reaction:changed", (data) => {
      eventsRef.current?.onReactionChanged?.(data);
    });
    socket.on("internal:message:updated", (data) => {
      eventsRef.current?.onMessageUpdated?.(data);
    });
    socket.on("internal:message:deleted", (data) => {
      eventsRef.current?.onMessageDeleted?.(data);
    });
    socket.on("internal:presence:changed", (data) => {
      eventsRef.current?.onPresenceChanged?.(data);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const joinChannel = useCallback((channelId: string) => {
    socketRef.current?.emit("join:channel", channelId);
  }, []);

  const joinChannels = useCallback((channelIds: string[]) => {
    if (channelIds.length === 0) return;
    socketRef.current?.emit("join:channels", channelIds);
  }, []);

  const leaveChannel = useCallback((channelId: string) => {
    socketRef.current?.emit("leave:channel", channelId);
  }, []);

  const startTyping = useCallback((channelId: string) => {
    socketRef.current?.emit("typing:start", { channelId });
  }, []);

  const stopTyping = useCallback((channelId: string) => {
    socketRef.current?.emit("typing:stop", { channelId });
  }, []);

  const sendHeartbeat = useCallback((userName?: string) => {
    socketRef.current?.emit("heartbeat", { userName });
  }, []);

  return { connected, joinChannel, joinChannels, leaveChannel, startTyping, stopTyping, sendHeartbeat };
}
